import { useState, type ImgHTMLAttributes } from 'react'
import { generatePlaceholderUrl } from '#utils'

/**
 * A bay photo that degrades to the shared placeholder.
 *
 * Call sites already fell back to `generatePlaceholderUrl` when a bay had *no*
 * image, but rendered a broken image when a stored URL resolved to nothing — the
 * failure mode when a storage object goes missing while `bays.image` still points
 * at it. That gap is what this closes, so every bay surface shows a placeholder
 * rather than a torn image icon.
 *
 * @example
 * <BayImage src={bay.image?.url} seed={bay.id} alt={bay.name} className="..." />
 */
export interface BayImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Stored image URL; falsy renders the placeholder directly. */
  src?: string | null
  /** Picks which placeholder to use — pass a stable value (usually the bay id). */
  seed: number
  alt: string
}

export function BayImage({ src, seed, alt, ...imgProps }: BayImageProps) {
  // State rather than the usual imperative `target.src = placeholder` swap in
  // `onError`: mutating the DOM node is undone by the next React render, and it
  // leaves the element permanently pinned to the placeholder even after the prop
  // changes to a working URL. Storing *which* URL failed (not a boolean) means a
  // later upload — a genuinely different object under a new path — gets a fresh
  // attempt on its own, with no effect needed to reset a flag.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const placeholder = generatePlaceholderUrl(seed)
  const resolved = !src || src === failedSrc ? placeholder : src

  return (
    <img
      {...imgProps}
      src={resolved}
      alt={alt}
      onError={(event) => {
        // Only fall back once — if the placeholder itself fails (missing
        // VITE_SUPABASE_URL in local dev), retrying would loop forever.
        if (src && resolved !== placeholder) setFailedSrc(src)
        imgProps.onError?.(event)
      }}
    />
  )
}
